import { useMutation } from "@tanstack/react-query";
import { message } from "antd";
import { AxiosError, HttpStatusCode } from "axios";
import { useAuth } from "context/AuthContextProvider";
import { ErrorResponse, useLocation, useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { ChangePasswordForm, LoginForm, ResetPasswordForm, VerifyAccountForm } from "shared/types";
import { ResponseHandler } from "shared/types/axios";
import { getApiErrorMessage, getApiSuccessMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { isSystemAdmin } from "shared/utils/role";
import { toE164OrRaw } from "shared/utils/phone";

const useLogin = () => {
  const { signIn, recaptchaVerifier } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return useMutation({
    mutationFn: async (loginForm: LoginForm) => {
      return client.post("/v1/auth/login", loginForm);
    },
    onSuccess: ({ data: response }) => {
      const details = response.data;
      signIn(details.jwtToken, details.refreshToken, details.user, details.business);
      // Redirect-back: ProtectedRoutes stashes the page that bounced the
      // visitor to login (e.g. the /customer?ref=<CODE> referral deep link);
      // honor it before the role defaults.
      const from = location.state as { path?: string; search?: string } | null;
      if (from?.path) {
        navigate(`${from.path}${from.search ?? ""}`, { replace: true });
      } else if (isSystemAdmin(details.user.roles)) {
        navigate(PATHS.BUSINESS);
      } else {
        navigate(PATHS.HOME);
      }
    },
    onError: async (error: AxiosError<ResponseHandler>) => {
      // Phone Number not authenticated
      try {
        if (error.response?.status === HttpStatusCode.Forbidden) {
          // The 403 carries the raw DB value, which pre-backfill rows still
          // hold as bare local digits ("81234567890"). Firebase requires E.164.
          const phone_number = toE164OrRaw(error.response.data.data);
          await recaptchaVerifier(phone_number);
          // A forgot-password attempt earlier in this tab must not outlive a
          // login: VerifyOTP reads verify_number first, and its Back goes to
          // the login page only when reset_number is not also present.
          sessionStorage.removeItem("reset_number");
          sessionStorage.setItem("verify_number", phone_number);
          navigate(PATHS.VERIFICATION);
          message.info(`${translate("messages.information.verify_phone")}`);
        } else {
          message.error(translate("messages.error.incorrect_login_credentials"));
        }
      } catch (err) {
        // Firebase rejections never reach the server, so surface the code here.
        console.error("Phone verification failed", (err as any)?.code, err);
        message.error(translate("messages.error.general"));
      }
    },
  });
};

const useVerifyAccount = () => {
  const navigate = useNavigate();
  const { clearPhoneProof, signIn } = useAuth();
  return useMutation({
    mutationFn: async (form: VerifyAccountForm) => {
      return client.post("/v1/auth/verify", form);
    },
    onSuccess: ({ data: response }) => {
      getApiSuccessMessage(`${translate("messages.success.verify_phone")}`);
      // The server signs the admin in on a successful first-login
      // verification (they have just proven password AND phone), answering
      // the same payload as /login. An older server, or an account login
      // would refuse anyway, sends no token: back to the login page as before.
      const details = response.data;
      if (details?.jwtToken) {
        signIn(details.jwtToken, details.refreshToken, details.user, details.business);
        // replace, unlike useLogin's push: onSettled clears verify_number, so
        // a Back onto /verify would hit its guard with no key and land on
        // forgot-password. Replacing leaves [/login, home], and AuthLayout's
        // signed-in guard absorbs that Back the same way it does after login.
        navigate(isSystemAdmin(details.user.roles) ? PATHS.BUSINESS : PATHS.HOME, {
          replace: true,
        });
      } else {
        navigate(PATHS.LOGIN);
      }
    },
    onError: (error: AxiosError<ResponseHandler>) => {
      getApiErrorMessage(error);
      navigate(PATHS.LOGIN);
    },
    onSettled: () => {
      // Ends the Firebase phone session only; the dashboard session set by
      // signIn above lives in our own storage.
      clearPhoneProof();
      sessionStorage.removeItem("verify_number");
    },
  });
};

const useChangePhone = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (data: { user_id: string; phone_number: string }) => {
      return client.post("/v1/auth/change-phone", data);
    },
    onSuccess: ({ data: response }) => {
      getApiSuccessMessage(`${translate("label.phone_number")} ${translate("messages.success.update")}`);
      signOut();
      navigate(PATHS.LOGIN);
    },
    onError: (error: AxiosError<ResponseHandler>) => {
      getApiErrorMessage(error);
    },
  });
};

const useFindUserByPhoneNumber = () => {
  return useMutation({
    mutationFn: async ({ phoneNumber, userType }: { phoneNumber: string; userType: string }) => {
      return client.post(`/v1/user/find-by-phone`, {
        phone_number: phoneNumber,
        user_type: userType,
      });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      throw error;
    },
  });
};

const useResetPassword = () => {
  const navigate = useNavigate();
  const { clearPhoneProof } = useAuth();
  return useMutation({
    mutationFn: async (resetPasswordForm: ResetPasswordForm) => {
      return client.post("/v1/auth/reset-password", resetPasswordForm);
    },
    onSuccess: ({ data: response }) => {
      getApiSuccessMessage(`${translate("label.new_password")} ${translate("messages.success.update")}`);
      clearPhoneProof();
      sessionStorage.removeItem("reset_number");
      navigate(PATHS.LOGIN);
    },
    onError: (error: AxiosError<ResponseHandler>) => {
      getApiErrorMessage(error);
      // A rejected proof cannot be retried; get a fresh OTP.
      if (error.response?.status === HttpStatusCode.Unauthorized) {
        clearPhoneProof();
        sessionStorage.removeItem("reset_number");
        navigate(PATHS.FORGET_PASSWORD);
      }
    },
  });
};

const useChangePassword = () => {
  return useMutation({
    mutationFn: async (changePasswordForm: ChangePasswordForm) => {
      return client.post("/v1/auth/change-password", changePasswordForm);
    },
    onSuccess: ({ data: response }) => {
      getApiSuccessMessage(`${translate("label.new_password")} ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ResponseHandler>) => {
      message.error(translate("messages.error.incorrect_login_credentials"));
    },
  });
};

export {
  useLogin,
  useChangePhone,
  useVerifyAccount,
  useResetPassword,
  useFindUserByPhoneNumber,
  useChangePassword,
};
